import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Trash2 } from "lucide-react";
import { rollUpLineItems } from "@/lib/rollUpLineItems";
import { cleanDescription } from "./workOrderTabUtils";

// Work-Order line-items grid (rolled up by part_number/name). The
// underlying flat `form.line_items` array stays in parent state; this
// component just renders the editable rolled-up rows and writes back
// via the supplied `setForm`.
//
// Manual "+ Material / + Labor" rows are intentionally absent — WOs
// are BOM-driven and origin-mutations happen via Change Orders.
// Extracted from WorkOrderTab.jsx (Feb 2026, iter175).
export function WOLineItemsTable({ form, setForm, isEditable, fmt }) {
  const lineItems = form.line_items || [];
  const rolled = rollUpLineItems(lineItems, !!form.show_breakdown);

  return (
    <div className="border rounded overflow-x-auto" data-testid="wo-line-items-table">
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50">
            <TableHead className="text-xs font-semibold w-24">Part #</TableHead>
            <TableHead className="text-xs font-semibold">Name</TableHead>
            <TableHead className="text-xs font-semibold w-20 text-center">Qty</TableHead>
            {form.show_breakdown && (
              <TableHead className="text-xs font-semibold w-24 text-right" data-testid="wo-col-material">Material</TableHead>
            )}
            {form.show_breakdown && (
              <TableHead className="text-xs font-semibold w-24 text-right" data-testid="wo-col-labor">Labor</TableHead>
            )}
            <TableHead className="text-xs font-semibold w-24 text-right">Total</TableHead>
            {isEditable && <TableHead className="text-xs font-semibold w-10"></TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rolled.map((item, idx) => {
            // Find all underlying flat line items that roll up into this
            // consolidated row, so qty / name edits cascade.
            // Prefer the exact source indices the rollup recorded. The
            // old key re-derivation drifted from the rollup key (parens
            // in the name, synthetic EQMT/DISP/TRVL part numbers), which
            // made delete + qty edits silently no-op on those rows.
            const matchKey =
              (item.part_number || "").trim().toLowerCase()
              || (item.name || item.description || "").trim().toLowerCase();
            const matchedIndices = item.source_indices?.length
              ? item.source_indices
              : lineItems.reduce((acc, li, i) => {
                  const liKey =
                    (li.part_number || "").trim().toLowerCase()
                    || cleanDescription(li.description || "").trim().toLowerCase();
                  if (liKey === matchKey) acc.push(i);
                  return acc;
                }, []);

            const handleQtyChange = (newQty) => {
              const q = parseInt(newQty) || 1;
              const items = [...lineItems];
              matchedIndices.forEach((i) => {
                const orig = items[i];
                const baseQty = orig.base_quantity || orig.quantity || 1;
                const ct = String(orig.cost_type || "").toLowerCase();
                if (ct === "labor" || ct === "1") {
                  // Labor scales from base hours-per-unit × new qty.
                  const hoursPerUnit = baseQty / (item.quantity || 1);
                  items[i] = {
                    ...orig,
                    quantity: Math.round(hoursPerUnit * q * 100) / 100,
                    base_quantity: hoursPerUnit,
                  };
                } else {
                  items[i] = { ...orig, quantity: q };
                }
              });
              setForm({ ...form, line_items: items });
            };

            const handleDelete = () => {
              if (!window.confirm(`Remove "${item.name || item.description}" from this Work Order?`)) return;
              const items = lineItems.filter((_, i) => !matchedIndices.includes(i));
              setForm({ ...form, line_items: items.length ? items : [] });
            };

            // Inline edit of the row's "Name" — writes the typed value
            // verbatim to a `custom_name` field on every underlying flat
            // line item that rolled up into this row. `rollUpLineItems`
            // surfaces it untrimmed so trailing spaces / characters the
            // user typed stick (a prior bug stripped them via dedup).
            // Raw `description` stays untouched so Budget/BOM/Procurement
            // keep reading the original part description.
            const handleNameChange = (newName) => {
              const items = [...lineItems];
              matchedIndices.forEach((i) => {
                items[i] = { ...items[i], custom_name: newName };
              });
              setForm({ ...form, line_items: items });
            };

            // iter318 — subcontract lines carry their own cost (per unit) so the
            // Foundation budget CSV can emit SUB $ at cost instead of 0.00.
            const ctRow = String(item.cost_type || "").toLowerCase();
            const isSub = ctRow === "3" || ctRow === "subcontract";
            const subCost = isSub ? (lineItems[matchedIndices[0]]?.subcontract_cost ?? 0) : null;
            const handleSubCostChange = (v) => {
              const items = [...lineItems];
              matchedIndices.forEach((i) => { items[i] = { ...items[i], subcontract_cost: parseFloat(v) || 0 }; });
              setForm({ ...form, line_items: items });
            };

            return (
              <TableRow key={item.id || idx}>
                <TableCell className="p-1">
                  <span className="text-xs font-mono text-slate-600">{item.part_number || "—"}</span>
                </TableCell>
                <TableCell className="p-1">
                  {isEditable ? (
                    <Input
                      type="text"
                      value={item.name || item.description || ""}
                      onChange={(e) => handleNameChange(e.target.value)}
                      className="h-7 text-xs px-1"
                      placeholder="Name / description"
                      data-testid={`wo-line-name-${idx}`}
                    />
                  ) : (
                    <span className="text-xs">{item.name || item.description}</span>
                  )}
                  {isSub && (
                    <div className="mt-0.5 flex items-center gap-1 text-[10px] text-slate-500" data-testid={`wo-line-subcost-${idx}`}>
                      <span>Sub cost / unit</span>
                      {isEditable ? (
                        <Input type="number" min="0" step="0.01" value={subCost}
                               onChange={(e) => handleSubCostChange(e.target.value)}
                               className="h-6 w-24 text-[10px] text-right px-1" data-testid={`wo-line-subcost-input-${idx}`} />
                      ) : <span className="font-mono">{fmt(subCost)}</span>}
                      {!(parseFloat(subCost) > 0) && (item.total || 0) > 0 && (
                        <span className="text-amber-700" data-testid={`wo-line-subcost-missing-${idx}`}>Sub cost missing — SUB $ exports 0.00</span>
                      )}
                    </div>
                  )}
                </TableCell>
                <TableCell className="p-1">
                  {isEditable ? (
                    <Input
                      type="number"
                      min="1"
                      value={item.quantity || 1}
                      onChange={(e) => handleQtyChange(e.target.value)}
                      className="h-7 text-xs text-right px-1"
                      data-testid={`wo-line-qty-${idx}`}
                    />
                  ) : (
                    <span className="text-xs text-right block">{item.quantity}</span>
                  )}
                </TableCell>
                {form.show_breakdown && (
                  <TableCell
                    className="text-xs text-right tabular-nums p-1"
                    data-testid={`wo-line-material-${idx}`}
                  >
                    {fmt(item.material_total || 0)}
                  </TableCell>
                )}
                {form.show_breakdown && (
                  <TableCell
                    className="text-xs text-right tabular-nums p-1"
                    data-testid={`wo-line-labor-${idx}`}
                  >
                    {fmt(item.labor_total || 0)}
                  </TableCell>
                )}
                <TableCell className="text-xs text-right font-medium p-1">
                  {fmt(item.total)}
                </TableCell>
                {isEditable && (
                  <TableCell className="p-1 text-right">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 text-slate-400 hover:text-red-500"
                      onClick={handleDelete}
                      data-testid={`wo-line-delete-${idx}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
